/**
 * Central Configuration for the Client Application
 * 
 * Configuration Priority (highest to lowest):
 * 1. Runtime config from runtime-config.js (no rebuild needed)
 * 2. Build-time environment variables (.env files)
 * 3. Default values
 * 
 * Configuration Options (in runtime-config.js):
 * - PUBLIC_URL: The public URL of the server (e.g., "https://test.gciad.cn:8443")
 *               Leave empty ('') to use relative URLs (nginx proxy mode)
 * - REACT_APP_USE_LOCAL_API: Set to "true" for local development (uses localhost:5001)
 * 
 * To change configuration without rebuilding:
 * 1. Edit client/build/runtime-config.js (or client/public/runtime-config.js before build)
 * 2. Refresh the browser (Ctrl+Shift+R to clear cache)
 */

// Runtime configuration interface
interface RuntimeConfig {
  PUBLIC_URL?: string;
  REACT_APP_USE_LOCAL_API?: string | boolean;
}

// Get runtime configuration (checked first, allows changes without rebuild)
const getRuntimeConfig = (): RuntimeConfig | null => {
  if (typeof window !== 'undefined') {
    // Check for runtime config from runtime-config.js file (loaded in index.html)
    const runtimeConfig = (window as any).__RUNTIME_CONFIG__ as RuntimeConfig | undefined;
    if (runtimeConfig && (runtimeConfig.PUBLIC_URL !== undefined || runtimeConfig.REACT_APP_USE_LOCAL_API !== undefined)) {
      return runtimeConfig;
    }
  }
  return null;
};

// Parse configuration values
const parseConfig = () => {
  const runtimeConfig = getRuntimeConfig();

  // If runtime config exists, use it exclusively (even if values are empty)
  // This ensures runtime-config.js can override build-time settings
  if (runtimeConfig) {
    const publicUrl = runtimeConfig.PUBLIC_URL ?? '';
    const useLocalValue = runtimeConfig.REACT_APP_USE_LOCAL_API;
    const useLocal = useLocalValue === 'true' || useLocalValue === true;

    return {
      publicUrl,
      useLocal,
      runtimeConfig
    };
  }

  // Fallback to build-time environment variables (only if no runtime config)
  const publicUrl = process.env.REACT_APP_PUBLIC_URL || '';
  const useLocalValue = process.env.REACT_APP_USE_LOCAL_API;
  const useLocal = useLocalValue === 'true';

  return {
    publicUrl,
    useLocal,
    runtimeConfig: null
  };
};

const { publicUrl, useLocal, runtimeConfig } = parseConfig();

// Determine final API_BASE_URL
// Priority: publicUrl > useLocal (localhost:5001) > empty (relative URLs for nginx proxy)
export const API_BASE_URL = publicUrl || (useLocal ? 'http://localhost:5001' : '');

/**
 * Resolve the backend base used by direct integrations such as the hosted MCP endpoint.
 * Relative API mode uses the page origin; configured API deployments use that explicit base.
 */
export const resolveBackendBaseUrl = (
  apiBaseUrl: string = API_BASE_URL,
  pageOrigin: string = typeof window !== 'undefined' ? window.location.origin : ''
): string => (apiBaseUrl || pageOrigin).replace(/\/+$/, '');

// Debug logging
console.log('🔧 Config Debug:', {
  'Runtime config': runtimeConfig ? 'loaded' : 'not found',
  'PUBLIC_URL': publicUrl || '(not set)',
  'useLocal': useLocal,
  'Final API_BASE_URL': API_BASE_URL || '(relative URLs)',
});

// Export additional config values
export const PUBLIC_URL = publicUrl;

/**
 * Rewrite secure-PDF URLs from the server so they always target THIS deployment.
 * Fixes responses that still carry an old absolute host (e.g. ztop.pro) when the
 * app is served from another domain/IP via nginx proxy or PUBLIC_URL.
 */
export function normalizeSecurePdfUrl(url: string): string {
  if (!url) return url;

  if (url.startsWith('/')) {
    return API_BASE_URL ? `${API_BASE_URL.replace(/\/$/, '')}${url}` : url;
  }

  try {
    const parsed = new URL(url);
    const targetOrigin = API_BASE_URL
      ? new URL(API_BASE_URL).origin
      : (typeof window !== 'undefined' ? window.location.origin : '');

    if (targetOrigin && parsed.origin !== targetOrigin) {
      const base = API_BASE_URL ? API_BASE_URL.replace(/\/$/, '') : '';
      return `${base}${parsed.pathname}${parsed.search}${parsed.hash}`;
    }
  } catch {
    // Not a valid absolute URL — return unchanged.
  }

  return url;
}

// Validation
if (!API_BASE_URL && !useLocal) {
  console.info('ℹ️ Using relative URLs (nginx proxy mode). API calls will go to /api/...');
}

if (API_BASE_URL) {
  console.log(`🎯 API calls will go to: ${API_BASE_URL}/api/...`);
}

